import React, { useState, useEffect, useCallback } from 'react';
import { Bike, ToggleLeft, ToggleRight, Percent, TrendingUp, Package, Settings2, Save, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_META = {
  grab: { name: 'Grab', color: '#00B14F' },
  foodpanda: { name: 'Foodpanda', color: '#D70F64' },
  line_man: { name: 'LINE MAN', color: '#00C300' },
  uber_eats: { name: 'Uber Eats', color: '#000000' },
  deliveroo: { name: 'Deliveroo', color: '#00CCBC' },
  just_eat: { name: 'Just Eat', color: '#FF8000' },
  glovo: { name: 'Glovo', color: '#FFC244' },
  wolt: { name: 'Wolt', color: '#009DE0' },
  bolt_food: { name: 'Bolt Food', color: '#34D186' },
  doordash: { name: 'DoorDash', color: '#FF3008' },
  grubhub: { name: 'Grubhub', color: '#F63440' },
  talabat: { name: 'Talabat', color: '#FF5800' },
  careem: { name: 'Careem', color: '#49B44D' },
  noon_food: { name: 'Noon Food', color: '#FEEE00' },
  rappi: { name: 'Rappi', color: '#FF441F' },
  ifood: { name: 'iFood', color: '#EA1D2C' },
  custom_delivery: { name: 'Livraison perso', color: '#8B5CF6' },
};

const REGION_LABELS = {
  asia: 'Asie',
  europe: 'Europe',
  north_america: 'Amerique du Nord',
  middle_east: 'Moyen-Orient',
  latin_america: 'Amerique Latine',
  global: 'Global',
};

export const OnlineSalesTab = ({ adminRole }) => {
  const [allPlatforms, setAllPlatforms] = useState({});
  const [activePlatforms, setActivePlatforms] = useState([]);
  const [commissions, setCommissions] = useState({});
  const [platformOrders, setPlatformOrders] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editCommissions, setEditCommissions] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [platRes, restRes, ordRes] = await Promise.allSettled([
        axios.get(`${API}/settings/delivery-platforms`),
        axios.get(`${API}/admin/restaurant-settings`),
        axios.get(`${API}/admin/orders`),
      ]);

      if (platRes.status === 'fulfilled') setAllPlatforms(platRes.value.data);

      if (restRes.status === 'fulfilled') {
        const rest = restRes.value.data;
        const active = rest.delivery_platforms || [];
        setActivePlatforms(active.map(p => typeof p === 'string' ? p : p.id));
        const comms = rest.platform_commissions || {};
        setCommissions(comms);
        setEditCommissions(comms);
      }

      // Count orders by platform
      if (ordRes.status === 'fulfilled') {
        const orders = ordRes.value.data;
        const counts = {};
        const totals = {};
        orders.forEach(o => {
          const src = o.order_source;
          if (src && src !== 'dine_in' && src !== 'takeaway' && src !== 'click_collect') {
            counts[src] = (counts[src] || 0) + 1;
            totals[src] = (totals[src] || 0) + (o.total || 0);
          }
        });
        setPlatformOrders({ counts, totals });
      }
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const togglePlatform = (platformId) => {
    setActivePlatforms(prev => {
      if (prev.includes(platformId)) return prev.filter(p => p !== platformId);
      return [...prev, platformId];
    });
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      // Save active platforms as objects with id and name
      const platformObjects = activePlatforms.map(pid => {
        const meta = PLATFORM_META[pid] || { name: pid };
        return { id: pid, name: meta.name };
      });

      await axios.patch(`${API}/admin/restaurant-settings`, {
        delivery_platforms: platformObjects,
        platform_commissions: editCommissions,
      });
      setCommissions(editCommissions);
      toast.success('Configuration sauvegardee');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  const formatPrice = (p) => `฿${(p || 0).toLocaleString()}`;

  const allPlatformsList = Object.entries(allPlatforms).map(([region, data]) => ({
    region,
    label: data.label || REGION_LABELS[region] || region,
    platforms: data.platforms || [],
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <RefreshCw className="w-6 h-6 animate-spin text-[#6E5E52]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="online-sales-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#2D241D] flex items-center gap-2">
            <Bike className="w-6 h-6 text-[#E16833]" />
            Vente en ligne
          </h2>
          <p className="text-sm text-[#6E5E52] mt-1">Gerez vos plateformes de livraison et commissions</p>
        </div>
        {adminRole === 'admin' && (
          <Button onClick={saveConfig} disabled={saving}
            className="bg-[#0A422D] hover:bg-[#083323] text-white rounded-xl gap-2"
            data-testid="save-online-config">
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Sauvegarder
          </Button>
        )}
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]">
          <p className="text-xs text-[#6E5E52] mb-1">Plateformes actives</p>
          <p className="text-2xl font-black text-[#0A422D]" data-testid="active-platforms-count">{activePlatforms.length}</p>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]">
          <p className="text-xs text-[#6E5E52] mb-1">Total commandes plateforme</p>
          <p className="text-2xl font-black text-[#E16833]" data-testid="total-platform-orders">
            {Object.values(platformOrders.counts || {}).reduce((s, c) => s + c, 0)}
          </p>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]">
          <p className="text-xs text-[#6E5E52] mb-1">Revenu total plateformes</p>
          <p className="text-2xl font-black text-[#D4A853]" data-testid="total-platform-revenue">
            {formatPrice(Object.values(platformOrders.totals || {}).reduce((s, c) => s + c, 0))}
          </p>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3]">
          <p className="text-xs text-[#6E5E52] mb-1">Commission moyenne</p>
          <p className="text-2xl font-black text-[#6E5E52]" data-testid="avg-commission">
            {activePlatforms.length > 0 ? (
              Math.round(activePlatforms.reduce((s, p) => s + (editCommissions[p] || 0), 0) / activePlatforms.length)
            ) : 0}%
          </p>
        </div>
      </div>

      {/* Active platforms with commissions */}
      {activePlatforms.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#E5DCD3] overflow-hidden">
          <div className="px-4 py-3 bg-[#FAF7F2] border-b border-[#E5DCD3]">
            <h3 className="font-bold text-[#2D241D] flex items-center gap-2">
              <Settings2 className="w-4 h-4" /> Plateformes actives & Commissions
            </h3>
          </div>
          <div className="divide-y divide-[#F3EDE5]">
            {activePlatforms.map(pid => {
              const meta = PLATFORM_META[pid] || { name: pid, color: '#888' };
              const orderCount = platformOrders.counts?.[pid] || 0;
              const orderRevenue = platformOrders.totals?.[pid] || 0;
              return (
                <div key={pid} className="px-4 py-3 flex items-center gap-4" data-testid={`active-platform-${pid}`}>
                  <div className="flex items-center gap-2 min-w-[140px]">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: meta.color }} />
                    <span className="font-bold text-sm text-[#2D241D]">{meta.name}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Percent className="w-3.5 h-3.5 text-[#6E5E52]" />
                    <Input
                      type="number" min="0" max="50" step="0.5"
                      value={editCommissions[pid] || ''}
                      onChange={e => setEditCommissions(prev => ({ ...prev, [pid]: parseFloat(e.target.value) || 0 }))}
                      placeholder="0"
                      className="w-20 h-8 text-xs rounded-lg border-[#E5DCD3] text-center"
                      data-testid={`commission-input-${pid}`}
                      disabled={adminRole !== 'admin'}
                    />
                    <span className="text-xs text-[#6E5E52]">%</span>
                  </div>
                  <div className="flex-1" />
                  <div className="text-right">
                    <p className="text-xs text-[#6E5E52]">{orderCount} commande{orderCount !== 1 ? 's' : ''}</p>
                    <p className="text-xs font-bold text-[#0A422D]">{formatPrice(orderRevenue)}</p>
                  </div>
                  <button onClick={() => togglePlatform(pid)}
                    className="text-red-400 hover:text-red-600 transition-colors"
                    data-testid={`deactivate-${pid}`}
                    disabled={adminRole !== 'admin'}
                  >
                    <ToggleRight className="w-6 h-6 text-[#0A422D]" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* All platforms by region */}
      <div className="space-y-4">
        <h3 className="font-bold text-[#2D241D] flex items-center gap-2">
          <Package className="w-5 h-5 text-[#D4A853]" /> Toutes les plateformes disponibles
        </h3>
        {allPlatformsList.map(({ region, label, platforms }) => (
          <div key={region} className="bg-white rounded-2xl border border-[#E5DCD3] overflow-hidden">
            <div className="px-4 py-2 bg-[#FAF7F2] border-b border-[#E5DCD3]">
              <span className="text-xs font-bold text-[#6E5E52] uppercase tracking-wider">{label}</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 p-3">
              {platforms.map(p => {
                const isActive = activePlatforms.includes(p.id);
                const meta = PLATFORM_META[p.id] || { name: p.name, color: '#888' };
                return (
                  <button key={p.id}
                    onClick={() => adminRole === 'admin' && togglePlatform(p.id)}
                    className={`flex items-center gap-2.5 p-3 rounded-xl border-2 transition-all active:scale-95 ${
                      isActive ? 'border-current bg-opacity-10' : 'border-[#E5DCD3] bg-white hover:border-[#D4A853]'
                    }`}
                    style={isActive ? { borderColor: meta.color, backgroundColor: `${meta.color}10` } : {}}
                    data-testid={`platform-toggle-${p.id}`}
                    disabled={adminRole !== 'admin'}
                  >
                    <div className="w-4 h-4 rounded-full flex-shrink-0" style={{ backgroundColor: meta.color }} />
                    <span className={`text-sm font-bold ${isActive ? 'text-[#2D241D]' : 'text-[#6E5E52]'}`}>{meta.name}</span>
                    {isActive ? (
                      <ToggleRight className="w-5 h-5 ml-auto" style={{ color: meta.color }} />
                    ) : (
                      <ToggleLeft className="w-5 h-5 ml-auto text-[#D4D4D4]" />
                    )}
                    {p.has_api && (
                      <span className="text-[8px] bg-[#0A422D] text-white px-1.5 py-0.5 rounded-full font-bold">API</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
